import { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addDays,
  eachDayOfInterval,
} from 'date-fns';
import { zIndex } from 'ui/theme';
import { CampaignEvent } from 'modules/campaignsCalendar/hooks';
import { Overlay } from 'ui/overlay';
import { Spinner } from 'ui/spinner';
import { parseDate } from 'utils/datetime';
import { MonthlyView } from './MonthlyView';
import { WeeklyView } from './WeeklyView';
import { CalendarHeader } from './CalendarHeader';
import { CalendarViewType } from 'modules/campaignsCalendar/constants';

const CalendarContainer = styled.div`
  width: 100%;
`;

const StyledOverlay = styled(Overlay)`
  position: absolute;
`;

export const CampaignsCalendar = ({
  events,
  isLoading,
}: { events: CampaignEvent[] | undefined; isLoading: boolean }) => {
  const [currentDate, setCurrentDate] = useState(parseDate());
  const [viewType, setViewType] = useState<CalendarViewType>(CalendarViewType.Month);

  const days = useMemo(() => {
    if (viewType === CalendarViewType.Month) {
      const firstDayOfMonth = startOfMonth(currentDate);
      const firstDayOfCalendar = startOfWeek(firstDayOfMonth);
      const lastDayOfMonth = endOfMonth(currentDate);
      const lastDayOfCalendar = endOfWeek(lastDayOfMonth);

      return eachDayOfInterval({
        start: firstDayOfCalendar,
        end: lastDayOfCalendar,
      });
    } else if (viewType === CalendarViewType.Week) {
      const firstDayOfWeek = startOfWeek(currentDate);
      const lastDayOfWeek = endOfWeek(currentDate);

      return eachDayOfInterval({
        start: firstDayOfWeek,
        end: lastDayOfWeek,
      });
    }
    return [];
  }, [currentDate, viewType]);

  // weekdays names to the calendar
  const weekdays = Array(7)
    .fill(0)
    .map((_, i) => format(addDays(startOfWeek(parseDate()), i), 'EEEE'));

  return (
    <CalendarContainer data-test="campaigns-calendar">
      {isLoading && (
        <StyledOverlay data-test="campaigns-calendar-loading" zIndex={zIndex.loadingOverlay}>
          <Spinner />
        </StyledOverlay>
      )}

      <CalendarHeader
        currentDate={currentDate}
        viewType={viewType}
        setCurrentDate={setCurrentDate}
        setViewType={setViewType}
      />

      {viewType === CalendarViewType.Week ? (
        <WeeklyView days={days} events={events} />
      ) : (
        <MonthlyView days={days} weekdays={weekdays} currentDate={currentDate} events={events} />
      )}
    </CalendarContainer>
  );
};
